import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import { useContext } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./App.css";
import AppContext from "../Context/AppContext";
import Login from "./Screens/AuthScreens/Login/Login";
import Register from "./Screens/AuthScreens/Register/Register";
import RecoverPassOne from "./Screens/AuthScreens/RecoverPassOne";
import RecoverPassThree from "./Screens/AuthScreens/RecoverPassThree";
import EmployeesScreen from "./Screens/AuthScreens/EmployeesScreen";
import InviteEmployees from "./Screens/AuthScreens/InviteEmployees/InviteEmployees";
import Employees from "./Screens/AuthScreens/Employees/Employees";
import Customers from "./Screens/UsersScreen/Customers/Customers";
import NavBar from "./Partials/Sections/NavBar/NavBar";
import Loader from "./Partials/Elements/Loader/Loader";
import Dashboard from "./Screens/Dashboard/Dashboard";
import SideBar from "./Partials/Sections/SideBar/SideBar";
import UsersScreen from "./Screens/UsersScreen/UsersScreen";
import EmployeeScreen from "./Screens/AuthScreens/Employees/EmployeeScreen";
import Events from "./Screens/Events/Events";
import SocialMediaScreen from "./Screens/AppSettingsScreens/SocialMediaScreen/SocialMediaScreen";
import EventScreen from "./Screens/Events/EventScreens";
import Crews from "./Screens/Events/Crews/Crews";
import Shows from "./Screens/Events/Shows/Shows";
import HeroSliders from "./Screens/AppSettingsScreens/HeroSliders/HeroSliders";
import AppSettingsScreens from "./Screens/AppSettingsScreens/AppSettingsScreens";
import EventStatusScreens from "./Screens/Events/EventStatusScreens";
import Ads from "./Screens/AppSettingsScreens/Ads/Ads";
import Theaters from "./Screens/Theaters/Theaters";
import Entrances from "./Screens/Theaters/Entrances/Halls";
import HallSeats from "./Screens/Theaters/Entrances/HallSeats/HallSeats";
import TicketCategories from "./Screens/TicketCategories/TicketCategories";
import FeesScreen from "./Screens/Fees/Fees";
import PromoCode from "./Screens/Fees/PromoCode/PromoCode";
import ShowsPrice from "./Screens/Events/ShowsPrice/ShowsPrice";
import States from "./Screens/States/States";
import City from "./Screens/States/City/City";
import Country from "./Screens/States/Country/Country";
import Orders from "./Screens/Orders/Order";
import OrderItems from "./Screens/Orders/OrderItems/OrderItems";
import UpvoteEventUser from "./Screens/Events/UpvoteEventUser/UpvoteEventUser";
import SearchOrder from "./Screens/SearchOrder/SearchOrder";
import DuplicateOrders from "./Screens/DuplicateOrders/DuplicateOrders";
import ToofanAtHoytsInAustralia from "./Screens/ToofanAtHoytsInAustralia/ToofanAtHoytsInAustralia";
import Producers from "./Screens/Producers/Producers";
import ProducerScreen from "./Screens/Producers/ProducerScreen";
import InviteProducers from "./Screens/Producers/InviteProducers/InviteProducers";
import EventProducers from "./Screens/Events/EventProducers/EventProducers";

// Role constants
const ALLOWED_ROLES = ["employee", "admin", "superAdmin"];
const ADMINS_ONLY = ["admin", "superAdmin"];
const EVENT_STATUS_ROLES = ["producer", "employee", "admin", "superAdmin"];

const ProtectedRoute = ({ element, allowedRoles = ALLOWED_ROLES }) => {
  const { employee } = useContext(AppContext);

  if (!employee) {
    return <Navigate to="/login" />;
  }

  if (!allowedRoles.includes(employee.level)) {
    // Redirect producers to /eventsStatusScreen, others to /
    return employee.level === "producer" ? <Navigate to="/eventsStatusScreen" /> : <Navigate to="/" />;
  }

  return element;
};

function App() {
  const { employee, isLoading } = useContext(AppContext);

  return (
    <section>
      <BrowserRouter>
        <NavBar />
        <main>
          {employee && <SideBar />}
          <section className="screen-container">
            <Routes>
              <Route
                path="/login"
                element={
                  !employee ? (
                    <Login />
                  ) : EVENT_STATUS_ROLES.includes(employee.level) ? (
                    <Navigate to="/eventsStatusScreen" />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/register/:token"
                element={!employee ? <Register /> : <Navigate to="/eventsStatusScreen" />}
              />
              <Route path="/recover-pass-one" element={<RecoverPassOne />} />
              <Route
                path="/resetEmployeePassword/:token"
                element={<RecoverPassThree />}
              />
              <Route
                path="/employeesScreen"
                element={<ProtectedRoute element={<EmployeesScreen />} />}
              />
              <Route
                path="/employeeScreen"
                element={<ProtectedRoute element={<EmployeeScreen />} />}
              />
              <Route
                path="/usersScreen"
                element={<ProtectedRoute element={<UsersScreen />} />}
              />
              <Route
                path="/producerScreen"
                element={<ProtectedRoute element={<ProducerScreen />} />}
              />
              <Route
                path="/producers"
                element={
                  <ProtectedRoute
                    element={<Producers />}
                    allowedRoles={ADMINS_ONLY}
                  />
                }
              />
              <Route
                path="/producerInvites"
                element={<ProtectedRoute element={<InviteProducers />} />}
              />
              <Route
                path="/employeeInvites"
                element={<ProtectedRoute element={<InviteEmployees />} />}
              />
              <Route
                path="/employees"
                element={<ProtectedRoute element={<Employees />} />}
              />
              <Route
                path="/customers"
                element={<ProtectedRoute element={<Customers />} />}
              />
              <Route
                path="/"
                element={<ProtectedRoute element={<Dashboard />} />}
              />
              <Route
                path="/eventsStatusScreen"
                element={
                  <ProtectedRoute
                    element={<EventStatusScreens />}
                    allowedRoles={EVENT_STATUS_ROLES}
                  />
                }
              />
              <Route
                path="/events/:status"
                element={<ProtectedRoute element={<Events />} allowedRoles={EVENT_STATUS_ROLES} />}
              />
              <Route
                path="/eventsScreens/:eventId"
                element={<ProtectedRoute element={<EventScreen />} />}
              />
              <Route
                path="/events/crews/:eventId"
                element={<ProtectedRoute element={<Crews />} />}
              />
              <Route
                path="/event/producers/:eventId/:eventName"
                element={<ProtectedRoute element={<EventProducers />} />}
              />
              <Route
                path="/events/shows/:eventId"
                element={<ProtectedRoute element={<Shows />} />}
              />
              <Route
                path="/shows/prices/:showsId"
                element={<ProtectedRoute element={<ShowsPrice />} />}
              />
              <Route
                path="/event/upvoted/user/:eventName/:eventId"
                element={<ProtectedRoute element={<UpvoteEventUser />} />}
              />
              <Route
                path="/appSettings"
                element={<ProtectedRoute element={<AppSettingsScreens />} />}
              />
              <Route
                path="/socialLinks"
                element={<ProtectedRoute element={<SocialMediaScreen />} />}
              />
              <Route
                path="/heroSliders"
                element={<ProtectedRoute element={<HeroSliders />} />}
              />
              <Route
                path="/ads"
                element={<ProtectedRoute element={<Ads />} />}
              />
              <Route
                path="/theaters"
                element={<ProtectedRoute element={<Theaters />} />}
              />
              <Route
                path="/entrances/:theaterId"
                element={<ProtectedRoute element={<Entrances />} />}
              />
              <Route
                path="/halls/seatManagement/:hallId"
                element={<ProtectedRoute element={<HallSeats />} />}
              />
              <Route
                path="/ticket/categories"
                element={<ProtectedRoute element={<TicketCategories />} />}
              />
              <Route
                path="/fees"
                element={<ProtectedRoute element={<FeesScreen />} />}
              />
              <Route
                path="/promocodes"
                element={<ProtectedRoute element={<PromoCode />} />}
              />
              <Route
                path="/countries"
                element={<ProtectedRoute element={<Country />} />}
              />
              <Route
                path="/country/states/:countryId"
                element={<ProtectedRoute element={<States />} />}
              />
              <Route
                path="/state/city/:stateId"
                element={<ProtectedRoute element={<City />} />}
              />
              <Route
                path="/shows/orders/:showId"
                element={<ProtectedRoute element={<Orders />} />}
              />
              <Route
                path="/orderItems/:orderId"
                element={<ProtectedRoute element={<OrderItems />} />}
              />
              <Route
                path="/searchOrder"
                element={<ProtectedRoute element={<SearchOrder />} />}
              />
              <Route
                path="/duplicateOrder"
                element={<ProtectedRoute element={<DuplicateOrders />} />}
              />
              <Route
                path="/toofanAtHoytsInAustralia"
                element={<ProtectedRoute element={<ToofanAtHoytsInAustralia />} />}
              />
              <Route path="*" element={<Navigate to={employee && employee.level === "producer" ? "/eventsStatusScreen" : "/login"} />} />
            </Routes>
          </section>
        </main>
      </BrowserRouter>
      <ToastContainer />
      {isLoading && <Loader />}
    </section>
  );
}

export default App;